import React, { useEffect, useState } from "react";
import { useBackendLinksProvider } from "../../../Providers/BackendLinksProvider.tsx";
import { useEndpointProvider } from "../../../Providers/EndpointProvider";
import { useEnvironmentVariablesProvider } from "../../../Providers/EnvironmentVariablesProvider.tsx";
import ModernMUITable from "../../../Components/ModernMUITable";
import { localizeDate } from "../../../Computation/utilFunctions";
import { MobergButton, MobergButtonShape, MobergButtonVariant, MobergTheme } from "../../../Moberg";
import MobergSnackbar from "../../../Components/MobergSnackbar/MobergSnackbar";
import StatusBadge from "../components/StatusBadge";

const SelectGateway = (props) => {
    const { onNext: onNextProp, onClose, isModal = false } = props;
    const [isLoading, setIsLoading] = useState(true);
    const { LINKS } = useBackendLinksProvider();
    const endpointProvider = useEndpointProvider();
    const { environmentVariables } = useEnvironmentVariablesProvider();
    const [gatewayData, setGatewayData] = useState([]);
    const [error, setError] = useState(null);

    const calculateDuration = (startTime, endTime) => {
        if (!startTime) return "-";
    
        const start = new Date(startTime);
        const end = endTime ? new Date(endTime) : new Date();
        const diff = end - start;
    
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        if (days > 0) return `${days} days ${hours} hrs ${minutes} mins`;
        if (hours > 0) return `${hours} hrs ${minutes} mins`;
        return `${minutes} mins`;
    };

    useEffect(() => {
        const getGateways = async () => {
            setIsLoading(true);
            const body = {
                "site_id": environmentVariables.SITE_ID
            }

            try {
                const res = await endpointProvider.get(LINKS.AGGREGATOR.GET_GATEWAYS, body);
                const gateways = res?.gateways || [];

                const detailedGateways = gateways.map((gw) => ({
                    id: gw.gateway_id,
                    number: gw.gateway_name || `Gateway-${gw.gateway_id}`,
                    occupied: gw.gateway?.status || "Not Available",
                    patientName: gw.patient?.patient_name && gw.patient.patient_name.trim() !== "" && gw.patient.patient_name !== "Unknown" ? gw.patient.patient_name : "-",
                    patientMRN: gw.patient?.patient_id || "-",
                    startTime: gw.start_time || null,
                    endTime: gw.end_time || null,
                    gatewayName: gw.gateway?.gateway_name || "Not Available",
                    gatewayStatus: gw.gateway?.status || "Not Available",
                }));

                setGatewayData(detailedGateways);

            } catch (err) {
                setError('Failed to load MCP devices. Please try again.');
            } finally {
                setIsLoading(false);
            }
        }

        getGateways();
    }, [isModal])

    const handleSelectGateway = async (gatewayId) => {
        if (!gatewayId) return;
        
        try {
            const res = await endpointProvider.get(LINKS.AGGREGATOR.GET_GATEWAY_INFO, {
                gateway_id: parseInt(gatewayId, 10)
            });
            
            if (onNextProp) {
                onNextProp(gatewayId, res);
            }
        } catch (err) {
            setError('Failed to load device information. Please try again.');
        }
    };

    const columns = [
        {
            field: "select",
            headerName: "",
            width: 90,
            minWidth: 90,
            maxWidth: 90,
            headerAlign: "center",
            align: "center",
            sortable: false,
            filterable: false,
            hideable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                return (
                    <MobergButton
                        variant={MobergButtonVariant.CLEAR}
                        theme={MobergTheme.BLUE}
                        shape={MobergButtonShape.RECTANGLE}
                        onClick={() => handleSelectGateway(params.row.id)}
                        style={{ marginTop: '5px', height: '40px' }}
                    >
                        Select
                    </MobergButton>
                );
            },
        },
       
        {
            field: "occupied",
            headerName: "Status",
            minWidth: 120,
            flex: 0.25,
            visible: true,
            disableColumnMenu: true,
            renderCell: (params) => {
                return <StatusBadge status={params.row.occupied} />;
            }
        },
        {
            field: "number",
            headerName: "Device name",
            minWidth: 150,
            flex: 0.3,
            visible: true,
            disableColumnMenu: true,
        },
        {
            field: "patientMRN",
            headerName: "Patient ID",
            minWidth: 150,
            flex: 0.3,
            visible: true,
            disableColumnMenu: true,
        },
        {
            field: "patientName",
            headerName: "Patient name",
            minWidth: 150,
            flex: 0.3,
            visible: true,
            disableColumnMenu: true,
        },
       
        {
            field: "startTime",
            headerName: "Started",
            minWidth: 180,
            flex: 0.4,
            visible: true,
            disableColumnMenu: true,
            valueGetter: (value, row) => {
                if (!row.startTime) return "-";
                try {
                    return localizeDate(new Date(row.startTime));
                } catch (error) {
                    return "-";
                }
            }
        },
        {
            field: "duration",
            headerName: "Duration",
            minWidth: 200,
            flex: 0.4,
            visible: true,
            disableColumnMenu: true,
            valueGetter: (value, row) => {
                return calculateDuration(row.startTime, row.endTime);
            }
        },
    ];

    const tableToolbarProps = {
        createButton: { isShow: false },
        deleteButton: { isShow: false },
        filterButton: { isShow: false },
        exportButton: { isShow: false },
        searchInput: { 
            isShow: true,
            placeholder: "Search MCP Device by device name, patient ID or patient name"
        },
        columnButton: { isShow: false },
        mainTable: true
    };

    return (
        <div style={{ backgroundColor: "var(--color-surface-primary)", height: "100%", display: "flex", flexDirection: "column" }}>
            {/* Instructions */}
            <div style={{ 
                padding: '16px 30px 12px',
            }}>
                <div style={{
                    backgroundColor: 'var(--color-hover-neutral)',
                    borderRadius: '12px',
                    padding: '14px 18px',
                    fontSize: '13.5px',
                    color: 'var(--color-text-primary)',
                    lineHeight: '1.6',
                    letterSpacing: '-0.01em',
                    fontWeight: '400'
                }}>
                    If an MCP Device is turned <span style={{ fontWeight: '600', color: '#F44336' }}>off</span>, click <span style={{ fontWeight: '600', color: 'var(--color-accent-interactive)' }}>Select</span> to turn it on and assign it to a patient. If an MCP Device is turned <span style={{ fontWeight: '600', color: '#4CAF50' }}>on</span>, click <span style={{ fontWeight: '600', color: 'var(--color-accent-interactive)' }}>Select</span> to view its status and start, stop, pause, or unpause the device.
                </div>
            </div>

            <div style={{ 
                position: 'relative', 
                padding: '10px 30px 20px', 
                height: isModal ? 'auto' : 'calc(100vh - 180px)',
                minHeight: '500px',
                maxHeight: isModal ? 'none' : '82vh',
                flex: isModal ? 1 : 'none',
                overflow: isModal ? 'hidden' : 'visible'
            }}>
                <ModernMUITable
                    columns={columns}
                    tableData={gatewayData}
                    rowCount={gatewayData.length}
                    tableToolbarProps={tableToolbarProps}
                    supportsPageSize={false}
                    pageSizeOptions={[]}
                    isRowSelectable={() => false}
                    isCheckboxSelection={false}
                    checkboxSelection={false}
                    height="100%"
                    paginationMode="client"
                    sortingMode="client"
                    loading={isLoading}
                    initialState={{
                        pagination: {
                            paginationModel: { page: 0, pageSize: 10 }
                        }
                    }}
                />
            </div>
            
            {/* Error Notification */}
            <MobergSnackbar
                open={!!error}
                onClose={() => setError(null)}
                message={error}
                severity="error"
            />
        </div>
    );
};

export default SelectGateway;
